import React, { useRef, useState } from "react";
import { Modal } from "react-bootstrap";
import styled from "styled-components";
import { Button } from "../../form/Button";
import StyledText from "../../StyledComponents/StyledText";
import ShareLinkIcon from "../../../assets/icons/ShareLinkIcon";
import ArrowIcon from "../../../assets/icons/ArrowIcon";
import { CorporateTheme, CreateFormTheme } from "../../../utils/themes";
import { FormFieldControl } from "../FieldPreviews/FormFieldControl";
import { FieldDataTypeEnum, FormPage, FormFieldTest, FormRow } from "../../../types/types";
import { useTheme } from "../../../middleware/ThemeContext";
import CrossIcon from "../../../assets/icons/CrossIcon";

interface FormPreviewModalProps {
  pages: FormPage[];
  onClose: () => void;
  formTitle: string;
}

const FormPreviewModal: React.FC<FormPreviewModalProps> = ({
  pages,
  onClose,
  formTitle,
}) => {
  const { theme } = useTheme();
  const [currentStep, setCurrentStep] = useState(0);
  const currentPage = pages[currentStep];

  const handleStepChange = (step: number) => setCurrentStep(step);

  const getPreviewFormField = (formField: FormFieldTest): FormFieldTest => {
    if (!formField?.field) return formField;

    // Prefer the persisted instance-level config first. Some callers
    // hydrate the preview with stale nested field config, while the
    // top-level config carries the latest saved layout-field settings.
    const mergedConfig = {
      ...(formField.field?.config || {}),
      ...(formField?.config || {}),
    };
    return {
      ...formField,
      config: mergedConfig,
      field: {
        ...formField.field,
        config: mergedConfig,
      },
    };
  };

  const renderField = (formField: FormFieldTest) => {
    if (!formField.field) return null;

    const previewField = getPreviewFormField(formField);

    return (
      <FormFieldControl
        formField={previewField}
        value={''}
        onChange={() => { }}
        forPreview
      />
    );
  };

  const renderMultipleFieldsRow = (row: FormRow) => (
    <div key={row?.id} className="row px-2 component-row">
      <div className="col-12">
        <div className="component-container p-0 pb-1" >
          <div className="d-flex justify-content-between align-items-center mb-2">
            <StyledText fontSize="16px" fontWeight={600}>
              {row?.formFields[0]?.component?.name || row?.formFields[0]?.componentName}
            </StyledText>
          </div>
          <div className="component-fields-grid">
            {row?.formFields.map((formField, fieldIndex) => (
              <div key={`${row?.id}-${fieldIndex}`} className="component-field">
                {renderField(formField)}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  const renderSingleOrSplitRow = (row: FormRow) => {
    const leftField = row?.formFields.find(f => f?.alignment === 1);
    const rightField = row?.formFields.find(f => f?.alignment === 2);

    return (
      <PreviewRow key={row.id}>
        <div className={row.layout === 'SINGLE' ? 'col-12' : 'col-6'}>
          <RowFieldWrapper>
            {row.layout === 'SINGLE'
              ? row.formFields[0] && renderField(row?.formFields[0])
              : leftField && renderField(leftField)
            }
          </RowFieldWrapper>
        </div>

        {row.layout === 'MULTIPLE' && (
          <div className="col-6">
            {rightField && (
              <RowFieldWrapper>
                {renderField(rightField)}
              </RowFieldWrapper>
            )}
          </div>
        )}
      </PreviewRow>
    );
  };

  const renderRow = (row: FormRow) => {
    if (row?.layout === 'MULTIPLE' && row?.formFields.every(f => f?.alignment === 0)) {
      return renderMultipleFieldsRow(row);
    }
    return renderSingleOrSplitRow(row);
  };

  const sortedPages = [...pages].sort((a, b) => {
    const pageA = a.pageNumber ?? 0; // agar undefined/null ho to 0 le
    const pageB = b.pageNumber ?? 0;
    return pageA - pageB;
  });


  const scrollRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);

  const handleMouseDown = (e: React.MouseEvent) => {
    if (!scrollRef.current) return;
    setIsDragging(true);
    setStartX(e.pageX - scrollRef.current.offsetLeft);
    setScrollLeft(scrollRef.current.scrollLeft);
  };

  const handleMouseLeave = () => setIsDragging(false);
  const handleMouseUp = () => setIsDragging(false);

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging || !scrollRef.current) return;
    e.preventDefault();
    const x = e.pageX - scrollRef.current.offsetLeft;
    const walk = (x - startX) * 1; // scroll speed
    scrollRef.current.scrollLeft = scrollLeft - walk;
  };


  const renderStepper = () => (
    <TopStepperContainer theme={theme}>
      <StepperWrapper
        ref={scrollRef}
        onMouseDown={handleMouseDown}
        onMouseLeave={handleMouseLeave}
        onMouseUp={handleMouseUp}
        onMouseMove={handleMouseMove}
        style={{ cursor: isDragging ? "grabbing" : "grab" }}

      >
        {sortedPages?.map((page, index) => (
          <div className={index === sortedPages.length - 1 ? "" : "w-100"} key={index}
          >
            <Stepper className="d-flex align-items-center">
              <div>
                <StepCircle
                  active={index <= currentStep}
                  onClick={() => handleStepChange(index)}
                >
                  {index + 1}
                </StepCircle>
              </div>

              {index !== pages.length - 1 && (
                <Connector active={index < currentStep} />
              )}
            </Stepper>
            <StepLabel
              theme={theme}
              active={index <= currentStep}
            >{page?.title}</StepLabel>
          </div>
        ))}
      </StepperWrapper >
    </TopStepperContainer>
  );

  const renderHeader = () => (
    <div className="d-flex align-items-center justify-content-between px-4 py-4 border-bottom">
      {/* <Button
        theme={theme}
        type="button"
        $variant="preview"
        className="border d-flex align-items-center"
        onClick={onClose}
      >
        <ArrowIcon color={theme.CorporateTheme.colors.textSecondary} className="mb-1" />
        Back 1
      </Button> */}
      <StyledText
        fontSize="18px"
        fontWeight={700}
        color={CorporateTheme?.colors?.headerColor}
      >
        {formTitle}
      </StyledText>
      <div style={{ width: "100px" }} />
      <div className="cursor-pointer" onClick={onClose}><CrossIcon color={theme.CorporateTheme.colors.textSecondary} /></div>
      {/* <Button theme={theme} type="button" $variant="share" onClick={onClose}>
        <ShareLinkIcon />
        Share Form
      </Button> */}
    </div>
  );

  const renderEmptyState = () => (
    <EmptyPreviewState theme={theme}>
      <StyledText fontSize="16px" fontWeight={600} color="#666">
        This page is empty
      </StyledText>
      <StyledText fontSize="14px" color="#999">
        No fields have been added to this page
      </StyledText>
    </EmptyPreviewState>
  );

  return (
    <Modal
      show={true}
      onHide={onClose}
      size="lg"
      className="border-0"
      centered
      backdrop="static"
      style={{ backgroundColor: 'rgba(231, 224, 224, 1)', overflow: 'hidden' }}
    >
      {pages.length > 1 && renderStepper()}

      <StyledModalContent theme={theme} className="border-0">
        {renderHeader()}

        <FormPreviewContainer theme={theme} className="px-3 py-3">
          {currentPage?.formRows.length > 0
            ? currentPage?.formRows.map(renderRow)
            : renderEmptyState()
          }
        </FormPreviewContainer>
      </StyledModalContent>
    </Modal>
  );
};




// Styled Components
const StyledModalContent = styled.div`
  overflow: hidden;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  background: ${({ theme }) => theme.CreateFormTheme.colors.background};
`;
const TopStepperContainer = styled.div`

 margin-bottom:10px;
  border-radius: 12px;
   box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  background: ${({ theme }) => theme.CreateFormTheme.colors.background};
`
const StepperWrapper = styled.div`
  display: flex;
 
  justify-content:space-between;

 max-width:600px;
   margin:auto;
  overflow-x:auto;
  
  padding: 30px 0px 30px 30px;
  /* Hide scrollbar but keep scrolling */
  -ms-overflow-style: none;  /* IE and Edge */
  scrollbar-width: none;     /* Firefox */

  &::-webkit-scrollbar {
    display: none;           /* Chrome, Safari */
  }
`;



const Stepper = styled.div`
display:flex;
align-items:center;
min-width:80px;
`;



const StepCircle = styled.div<{ active: boolean }>`
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background-color: ${({ active }) => active ? "#002f6c" : "#c0c0c0"};
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  font-size: 14px;
  cursor: pointer;
`;

const StepLabel = styled.div<{ active: boolean }>`
  margin-top: 5px;
  font-size: 14px;
  font-weight: ${({ active }) => active ? 700 : 500};
  font-family: "Raleway", sans-serif;
  color: ${({ theme }) => theme.CorporateTheme.colors.textPrimary};
white-space: nowrap;
`;


const Connector = styled.div<{ active: boolean }>`
  height: 4px;
  margin: 0 5px;
  background-color: ${({ active }) => (active ? "#002f6c" : "#c0c0c0")};
    width:100%;
    border-radius:2px;

`;

const FormPreviewContainer = styled.div`
  min-height: 60vh;
  height: 60vh;
  max-height: 60vh;
  overflow-y: auto;
  background: ${({ theme }) => theme.CorporateTheme.colors.background};
  border-radius: 8px;
`;

const PreviewRow = styled.div`
  display: flex;
`;

const RowFieldWrapper = styled.div`
  width: 100%;
  padding: 8px 12px;
  display: flex;
  align-items: center;
  min-height: 48px;
  box-sizing: border-box;
  gap: 8px;

  & > * {
    width: 100%;
  }
`;

const EmptyPreviewState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 200px;
  text-align: center;
  background: ${({ theme }) => theme.CorporateTheme.colors.formBuilderNormalBG};
  border-radius: 8px;
  margin: 20px;
`;

export default FormPreviewModal;